/**
 * Who's here — the roster screen's read and its five writes (issues #299, #303).
 *
 * **The read came first for somebody else.** `GET /roster` was added for the memory screen:
 * `remember` requires a `subject` on every propose and will not take a default (#92 — *"the
 * family's"* is a claim, not a cautious fallback), so a form that saves a fact has to let
 * somebody say whose it is, and naming a member needs their `userId`. #303 is what the read
 * was actually for.
 *
 * **The five writes are #298's routes onto the tools themselves**, and there is no
 * panel-shaped write here for the same reason there is none in `memory.ts`: removing a member
 * from this screen *is* `remove_member` — the same plain function, the same tier, the same
 * `PROPOSAL#`, the same permanent `ACTION#` (#109) — which is the whole of ADR 0021
 * decision 5. Every one of them proposes and then confirms with `{ confirm, proposalId }` and
 * no body, so a paraphrase cannot change who a write is about. None of them carries a
 * `provenance` field, which is ADR 0014 decision 4's structural floor: no phrasing and no
 * `ai_write_mode` removes somebody in one turn.
 *
 * **The response types are a subset of what the server discloses**, deliberately (ADR 0021
 * decision 6, and `memory.ts`'s rule) — a field the screen does not render is a field this
 * file should not claim to understand. {@link InviteEnvelope} is where that matters most:
 * see its note on `inviteLink`.
 */

import { type AppApi, appFetch } from "./client";
import {
  confirmWrite,
  familyPath,
  newIdempotencyKey,
  postWrite,
  type WriteEnvelope,
} from "./writes";

/**
 * Re-exported so the roster screen's imports stay one module deep (issue #310 moved the
 * bodies out). See `writes.ts`.
 */
export { newIdempotencyKey };

export interface RosterMember {
  readonly userId: string;
  readonly name: string;
  /**
   * Their address, used **only** to tell two members with the same display name apart.
   *
   * The service supports that case deliberately — #92 compares subjects by `memberId` — and a
   * picker showing two identical options is how a durable fact gets assigned to the wrong
   * person. Optional because nothing else here needs it, and `FactForm` appends it only where
   * a name is actually shared.
   */
  readonly email?: string;
  readonly role: string;
  /** ISO instant. `null` on a record written before the field existed. */
  readonly joinedAt?: string | null;
  /**
   * Which row is the caller — **the server's answer, never a comparison made here** (#303).
   *
   * It is what decides whether a row offers *Leave this family* or *Remove*, and the only
   * string this bundle holds about the signed-in person is their address. Comparing that
   * against `email` would be wrong the day a member's provider address diverges from the one
   * on their `MEMBER#` row, and wrong in the expensive direction.
   */
  readonly isYou?: boolean;
}

/** One invitation that has been sent and not yet taken up. */
export interface PendingInvitation {
  readonly email: string;
  readonly role: string;
  /** The member who sent it, or `null` when they have since left the family. */
  readonly invitedBy: string | null;
  readonly invitedAt: string;
  readonly expiresAt: string;
  /**
   * Lapsed, but still on the record — the server computes it so this screen cannot get the
   * clock wrong. A lapsed invitation is still shown and still cancellable, because whoever
   * holds the link has no way to know it stopped working.
   */
  readonly expired: boolean;
}

export interface Roster {
  readonly members: readonly RosterMember[];
  readonly invitations: readonly PendingInvitation[];
  /**
   * Whether this caller may change any of it.
   *
   * **Absent reads as `false`**, which is the safe direction: an older bundle against a server
   * that never sent it shows the roster and offers no control, and every control it would have
   * offered is one the server refuses anyway.
   */
  readonly canAdminister?: boolean;
}

export function fetchRoster(api: AppApi, familyId: string): Promise<Roster> {
  return appFetch<Roster>(api, familyPath(familyId, "/roster"));
}

/**
 * What a roster write answers with — the tools' shared envelope (ADR 0006 decision 5).
 *
 * An alias since #310 rather than a fourth copy of the same four fields: `status` is still the
 * field to branch on, never the HTTP code, because #298 answers `200` for both a proposal and
 * an apply and the envelope's own status is the structural statement. The name stays because
 * this screen's own extensions ({@link DisbandEnvelope}) read as extensions *of the roster's*
 * envelope.
 */
export type RosterWriteEnvelope = WriteEnvelope;

/**
 * `invite_member`'s envelope, **which carries no link on this channel** (#303).
 *
 * The tool returns the one-time onboarding link on the confirmed apply so the inviter can
 * relay it as a fallback when mail does not arrive. That is right for a conversation, where
 * the person asking is the person who would relay it. It is wrong for this screen, which shows
 * no link and offers no way to pass one on — so a live single-use credential would sit in a
 * browser's memory, its devtools and any error report taken from that tab, for nothing.
 *
 * **The suppression is the server's, and it has to be** (PR #330 review). Not declaring the
 * field here hides it from TypeScript and from nothing else: `writeHandler` serialises the
 * tool's envelope unchanged and `appFetch` parses the whole body, so the token would still be
 * in the response bytes. `mayRelayInviteLink` in `services/mcp/src/tools/inviteMember.ts`
 * withholds both the field and the URL in `message` for `arrivedVia: "web"`, and
 * `tests/app/writes.test.ts` asserts it on the bytes rather than on the DOM.
 *
 * It stays undeclared as well, for `memory.ts`'s reason: a field the screen does not render is
 * a field this file should not claim to understand.
 */
export type InviteEnvelope = RosterWriteEnvelope;

/** The `deleting` counts and limits `disband_family` discloses before a family answers (#228). */
export interface DisbandEnvelope extends RosterWriteEnvelope {
  /** What deletion does not reach, in the server's own sentences. Sent on both replies. */
  readonly doesNotReach: readonly string[];
  /**
   * Whether the sweep actually finished. `null` while proposing.
   *
   * Reported honestly because the commit and the erasure are separate steps (#228): the
   * confirm writes a marker and the sweep runs after it, so `status: "applied"` alone would
   * tell a family their data was gone when some of it is still there.
   */
  readonly erased: boolean | null;
}

function post<T extends RosterWriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: RosterWriteTail,
  body: Record<string, unknown>,
): Promise<T> {
  return postWrite<T>(api, familyId, tail, body);
}

/**
 * The five routes this screen uses, named once — a **narrowing** of `writes.ts`'s
 * {@link WriteTail} rather than a second list of routes.
 *
 * A union rather than a bare string, so {@link confirmRosterWrite} cannot be handed a tail
 * that names no tool — which matters more here than on the memory screen, because
 * `confirmProposal`'s `expectedTool` (#107) refuses a proposal confirmed through the wrong
 * one, and getting it wrong would turn *"yes, remove them"* into a refusal a family cannot
 * act on.
 */
export type RosterWriteTail =
  | "/invite-member"
  | "/revoke-invitation"
  | "/remove-member"
  | "/leave-family"
  | "/transfer-ownership"
  | "/disband-family";

/**
 * Invite somebody in.
 *
 * **`role` is always `adult` and there is no picker**, which is the canvas's own shape: the
 * invite dialog collects an address and nothing else. `owner` is reachable — it is *Make
 * owner* on the member's row once they are here — and offering it at invitation time would
 * mean handing co-ownership to an address before anybody has confirmed it is theirs.
 */
export function proposeInvite(
  api: AppApi,
  familyId: string,
  email: string,
): Promise<InviteEnvelope> {
  return post<InviteEnvelope>(api, familyId, "/invite-member", {
    email,
    role: "adult",
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Call an invitation back.
 *
 * **Addressed by email — the one roster write that is not addressed by an id.** The `INVITE#`
 * sort key is the SHA-256 of the emailed token and is not ours to hand a browser (#115), and
 * for the case this mostly exists for — a mistyped address — the typo is the only string the
 * family has. It withdraws *every* still-pending invitation to that address.
 */
export function proposeRevoke(
  api: AppApi,
  familyId: string,
  email: string,
): Promise<RosterWriteEnvelope> {
  return post(api, familyId, "/revoke-invitation", {
    email,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function proposeRemove(
  api: AppApi,
  familyId: string,
  memberId: string,
): Promise<RosterWriteEnvelope> {
  return post(api, familyId, "/remove-member", {
    memberId,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function proposeTransfer(
  api: AppApi,
  familyId: string,
  toMemberId: string,
): Promise<RosterWriteEnvelope> {
  return post(api, familyId, "/transfer-ownership", {
    toMemberId,
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Leave.
 *
 * **No target field at all**, and the absence is the authorization boundary rather than a
 * convenience (#115): there is exactly one member this could remove and the session already
 * fixes them. A `memberId` here would be a way to take somebody else out through a tool that
 * needs no capability.
 */
export function proposeLeave(api: AppApi, familyId: string): Promise<RosterWriteEnvelope> {
  return post(api, familyId, "/leave-family", { idempotencyKey: newIdempotencyKey() });
}

export function proposeDisband(api: AppApi, familyId: string): Promise<DisbandEnvelope> {
  return post<DisbandEnvelope>(api, familyId, "/disband-family", {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * Apply a stored proposal: `{ confirm: true, proposalId }` and **nothing else**.
 *
 * The body-free confirm is ADR 0011's whole mechanism, so this function takes no payload to
 * send. It is what answers #303's worry that *"remove my husband" is a terrible thing to get
 * wrong from a paraphrase*: on this surface there is no paraphrase — a member is chosen from a
 * list — and the confirm still carries nothing that could change who the write is about.
 */
export function confirmRosterWrite<T extends RosterWriteEnvelope>(
  api: AppApi,
  familyId: string,
  tail: RosterWriteTail,
  proposalId: string,
): Promise<T> {
  return confirmWrite<T>(api, familyId, tail, proposalId);
}
